import React, { useEffect, useState } from "react";
import "./Search.css";
import { FaSearch } from "react-icons/fa";
import HomeHeader from "../../components/header/HomeHeader";
import CommonFooter from "../../components/footer/CommonFooter";
import ProductCard from "../home/components/ProductCard";
import { appApiHandler } from "../../api/appApiHandler";
import FullScreenLoader from "../../components/loader/fullScreenLoader/FullscreenLoader";
import Breadcrumb from "../../components/breadcrumb";
import EmptyState from "../../components/EmptyState";
import { getUserType } from "../../constants/asyncStoreManager";
import { IoIosArrowDown } from "react-icons/io";
import { IoFilter } from "react-icons/io5";
import { IoClose } from "react-icons/io5";
import { Range } from "react-range";
import { colors } from "../../constants/colors";
import "../../styles/filterColorSwatches.css";

const PRICE_UNDER_OPTIONS = [
  { value: 999, label: "Under ₹999" },
  { value: 1999, label: "Under ₹1999" },
  { value: 2999, label: "Under ₹2999" },
  { value: 4999, label: "Under ₹4999" },
  { value: 7999, label: "Under ₹7999" },
  { value: 14999, label: "Under ₹14999" },
  { value: 19999, label: "Under ₹19999" },
  { value: 24999, label: "Under ₹24999" }
];

const SearchPage = () => {
  const [searchText, setSearchText] = useState("");
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [showSortDropdown, setShowSortDropdown] = useState(false);

  // Filter data
  const [fabrics, setFabrics] = useState([]);
  const [brands, setBrands] = useState([]);
  const [sizes, setSizes] = useState([]);
  const [filterColors, setFilterColors] = useState([]);

  // Filters state
  const [selectedSortOption, setSelectedSortOption] = useState("newest");
  const [selectedFabrics, setSelectedFabrics] = useState([]);
  const [selectedBrands, setSelectedBrands] = useState([]);
  const [selectedSizes, setSelectedSizes] = useState([]);
  const [selectedColors, setSelectedColors] = useState([]);
  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");

  //filter open/close state
  const [isPriceOpen, setIsPriceOpen] = useState(false);
  const [isFabricsOpen, setIsFabricsOpen] = useState(false);
  const [isBrandsOpen, setIsBrandsOpen] = useState(false);
  const [isSizesOpen, setIsSizesOpen] = useState(false);
  const [isColorsOpen, setIsColorsOpen] = useState(false);

  const [tempFabrics, setTempFabrics] = useState([]);
  const [tempBrands, setTempBrands] = useState([]);
  const [tempSizes, setTempSizes] = useState([]);
  const [tempColors, setTempColors] = useState([]);

  const [selectedPriceUnder, setSelectedPriceUnder] = useState(null);
  const [tempPriceUnder, setTempPriceUnder] = useState(null);

  const [priceRange, setPriceRange] = useState([0, 50000]);

  const getFilterParams = async () => {
    const response = await appApiHandler.getProductFilterParams();
    if (response?.status === 200) {
      if (response?.fabrics) {
        setFabrics(response?.fabrics);
      }
      if (response?.brands) {
        setBrands(response?.brands);
      }
      if (response?.sizes) {
        setSizes(response?.sizes);
      }
      if (response?.colors) {
        setFilterColors(response?.colors);
      }
    }
  };

  const getSearchProducts = async () => {
    setLoading(true);
    const userType = (await getUserType()) || "retailer";
    const reqData = {
      search: searchText,
      user_type: userType,
      fabrics: selectedFabrics,
      brands: selectedBrands,
      sizes: selectedSizes,
      colors: selectedColors || [],
      min_price: minPrice !== "" ? minPrice : 0,
      max_price: maxPrice !== "" ? maxPrice : 50000,
      sort_type: selectedSortOption,
    };
    const response = await appApiHandler.filterProducts(reqData);
    setLoading(false);
    if (response?.status === 200) {
      setProducts(response?.data || []);
    }
  };
  const togglePriceUnder = (value) => {
    const newVal = tempPriceUnder === value ? null : value;
    setTempPriceUnder(newVal);
    // Sync with main price state to trigger immediate update
    if (newVal) {
      setPriceRange([0, newVal]);
      setMinPrice(0);
      setMaxPrice(newVal);
      setSelectedPriceUnder(newVal);
    } else {
      setPriceRange([0, 50000]);
      setMinPrice("");
      setMaxPrice("");
      setSelectedPriceUnder(null);
    }
  };

  const toggleFromArray = (array, setter, value) => {
    const exists = array.includes(value);
    const next = exists ? array.filter((v) => v !== value) : [...array, value];
    setter(next);
  };

  const handleApplyAll = () => {
    setMinPrice(priceRange[0]);
    setMaxPrice(priceRange[1]);
    setSelectedFabrics(tempFabrics);
    setSelectedBrands(tempBrands);
    setSelectedSizes(tempSizes);
    setSelectedColors(tempColors);
    setSelectedPriceUnder(tempPriceUnder);
    setShowFilters(false);
  };

  const handleClearAll = () => {
    setTempFabrics([]);
    setTempBrands([]);
    setTempSizes([]);
    setTempColors([]);
    setTempPriceUnder(null);

    setMinPrice("");
    setMaxPrice("");
    setPriceRange([0, 50000]);
    setSelectedFabrics([]);
    setSelectedBrands([]);
    setSelectedSizes([]);
    setSelectedColors([]);
    setSelectedPriceUnder(null);
    setShowFilters(false);
  };

  const handleSortChange = (e) => {
    const value = e.target.value;
    setSelectedSortOption(value);
  };

  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };

  useEffect(() => {
    setTempFabrics(selectedFabrics);
    setTempBrands(selectedBrands);
    setTempSizes(selectedSizes);
    setTempColors(selectedColors);
    setTempPriceUnder(selectedPriceUnder);

    // Sync slider with current price values
    if (minPrice !== "" && maxPrice !== "") {
      setPriceRange([minPrice, maxPrice]);
    } else {
      setPriceRange([0, 50000]);
    }
  }, [
    minPrice,
    maxPrice,
    selectedFabrics,
    selectedBrands,
    selectedSizes,
    selectedColors,
    selectedPriceUnder,
  ]);

  useEffect(() => {
    getFilterParams();

    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    setLoading(true);
    getSearchProducts().finally(() => {
      setLoading(false);
    });
  }, [
    selectedSortOption,
    selectedFabrics,
    selectedBrands,
    selectedSizes,
    selectedColors,
    minPrice,
    maxPrice,
    selectedPriceUnder,
  ]);

  useEffect(() => {
    if (!searchText.trim()) {
      getSearchProducts();
    }
  }, [searchText]);

  const onPressSearch = () => {
    getSearchProducts(searchText);
  };

  // Auto search on typing with debounce
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchText.trim()) {
        getSearchProducts();
      } else {
        getSearchProducts();
      }
    }, 500); // 500ms delay

    return () => clearTimeout(timeoutId);
  }, [searchText]);

  return (
    <div className="searchPage">

      {/* <HomeHeader /> */}

      <div className="page-content">
        <Breadcrumb
          items={[
            {
              label: "Home",
              path: "/",
            },
            {
              label: "Search",
              isActive: true,
            },
          ]}
        />
        <main className="searchPage__main">
          {/* Filters Sidebar */}
          <div
            className={`searchFiltersBackdrop ${showFilters ? "show" : ""}`}
            onClick={() => setShowFilters(false)}
          />

          <section className={`searchFilters ${showFilters ? "show" : ""}`}>
            <div className="searchFiltersAccordion">
              <div className="searchFiltersHeader">
                <h3 className="searchFiltersHeader__title">FILTER</h3>
                <button
                  className="searchFiltersHeader__close"
                  onClick={() => setShowFilters(false)}
                >
                  <IoClose size={20} />
                </button>
              </div>

              <div className="searchFiltersContent">
                {/* Price */}
                <div className="searchAccordionSection">
                  <button
                    className="searchAccordionHeader"
                    onClick={() => setIsPriceOpen((v) => !v)}
                  >
                    <span>Price</span>
                    <IoIosArrowDown
                      size={18}
                      className={isPriceOpen ? "is-open" : ""}
                    />
                  </button>
                  {isPriceOpen && (
                    <div className="searchAccordionBody">
                      <div className="searchFiltersBar__priceInputs searchFiltersAccordion__price">
                        <div className="searchPrice-slider-container">
                          <Range
                            step={1}
                            min={0}
                            max={50000}
                            values={priceRange}
                            onChange={(values) => setPriceRange(values)}
                            renderTrack={({ props, children }) => {
                              const { key, ...trackProps } = props;
                              return (
                                <div
                                  key={key}
                                  {...trackProps}
                                  className="searchPrice-slider-track"
                                  style={{
                                    ...trackProps.style,
                                    height: "6px",
                                    width: "100%",
                                    backgroundColor: colors.lightGray2,
                                    borderRadius: "3px",
                                  }}
                                >
                                  <div
                                    className="searchPrice-slider-range"
                                    style={{
                                      left: `${(priceRange[0] / 50000) * 100}%`,
                                      width: `${((priceRange[1] - priceRange[0]) / 50000) * 100}%`,
                                    }}
                                  />
                                  {children}
                                </div>
                              );
                            }}
                            renderThumb={({ props }) => {
                              const { key, ...thumbProps } = props;
                              return (
                                <div
                                  key={key}
                                  {...thumbProps}
                                  className="searchPrice-slider-thumb"
                                  style={thumbProps.style}
                                />
                              );
                            }}
                          />
                          <span className="searchPrice-range-text">
                            Rs. {priceRange[0].toFixed(2)} — Rs.{" "}
                            {priceRange[1].toFixed(2)}
                          </span>
                          <div
                            className="searchFilters__priceUnderGroup"
                            role="group"
                            aria-label="Price under presets"
                          >
                            {PRICE_UNDER_OPTIONS.map(({ value, label }) => (
                              <button
                                key={value}
                                type="button"
                                className={`searchFilters__priceUnderBtn${
                                  tempPriceUnder === value ? " is-selected" : ""
                                }`}
                                aria-pressed={tempPriceUnder === value}
                                onClick={() => togglePriceUnder(value)}
                              >
                                {label}
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Fabrics */}
                {fabrics?.length > 0 && (
                  <div className="searchAccordionSection">
                    <button
                      className="searchAccordionHeader"
                      onClick={() => setIsFabricsOpen((v) => !v)}
                    >
                      <span>Fabrics</span>
                      <IoIosArrowDown
                        size={18}
                        className={isFabricsOpen ? "is-open" : ""}
                      />
                    </button>
                    {isFabricsOpen && (
                      <div className="searchAccordionBody">
                        <div className="searchFiltersBar__checkboxGrid">
                          {fabrics?.map((f) => (
                            <label key={f} className="searchFiltersBar__checkboxItem">
                              <input
                                type="checkbox"
                                checked={tempFabrics.includes(f)}
                                onChange={() =>
                                  toggleFromArray(
                                    tempFabrics,
                                    setTempFabrics,
                                    f
                                  )
                                }
                              />
                              <span>{f}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Brands */}
                {brands?.length > 0 && (
                  <div className="searchAccordionSection">
                    <button
                      className="searchAccordionHeader"
                      onClick={() => setIsBrandsOpen((v) => !v)}
                    >
                      <span>Brands</span>
                      <IoIosArrowDown
                        size={18}
                        className={isBrandsOpen ? "is-open" : ""}
                      />
                    </button>
                    {isBrandsOpen && (
                      <div className="searchAccordionBody">
                        <div className="searchFiltersBar__checkboxGrid">
                          {brands?.map((b) => (
                            <label key={b} className="searchFiltersBar__checkboxItem">
                              <input
                                type="checkbox"
                                checked={tempBrands.includes(b)}
                                onChange={() =>
                                  toggleFromArray(tempBrands, setTempBrands, b)
                                }
                              />
                              <span>{b}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Color */}
                {filterColors?.length > 0 && (
                  <div className="searchAccordionSection">
                    <button
                      type="button"
                      className="searchAccordionHeader"
                      onClick={() => setIsColorsOpen((v) => !v)}
                    >
                      <span>Color</span>
                      <IoIosArrowDown
                        size={18}
                        className={isColorsOpen ? "is-open" : ""}
                      />
                    </button>
                    {isColorsOpen && (
                      <div className="searchAccordionBody">
                        <div
                          className="filtersColorSwatchGrid"
                          role="group"
                          aria-label="Filter by color"
                        >
                          {filterColors.map((color) => {
                            const colorName = color?.name;
                            const swatch = color?.hex_code || "#000000";
                            const isSelected = tempColors.includes(colorName);

                            return (
                              <button
                                key={color?._id || colorName}
                                type="button"
                                className={`filtersColorSwatch${isSelected ? " is-selected" : ""
                                  }`}
                                aria-pressed={isSelected}
                                onClick={() =>
                                  toggleFromArray(
                                    tempColors,
                                    setTempColors,
                                    colorName,
                                  )
                                }
                              >
                                <span
                                  className="filtersColorSwatch__dot"
                                  style={
                                    String(swatch).includes("gradient")
                                      ? { background: swatch }
                                      : { backgroundColor: swatch }
                                  }
                                />
                                <span className="filtersColorSwatch__label">
                                  {colorName}
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Sizes */}
                {sizes?.length > 0 && (
                  <div className="searchAccordionSection">
                    <button
                      className="searchAccordionHeader"
                      onClick={() => setIsSizesOpen((v) => !v)}
                    >
                      <span>Size</span>
                      <IoIosArrowDown
                        size={18}
                        className={isSizesOpen ? "is-open" : ""}
                      />
                    </button>
                    {isSizesOpen && (
                      <div className="searchAccordionBody">
                        <div className="searchFiltersBar__checkboxGrid">
                          {sizes?.map((s) => (
                            <label key={s} className="searchFiltersBar__checkboxItem">
                              <input
                                type="checkbox"
                                checked={tempSizes.includes(s)}
                                onChange={() =>
                                  toggleFromArray(tempSizes, setTempSizes, s)
                                }
                              />
                              <span>{s}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="searchFiltersActions">
                <button className="searchBtnSecondary" onClick={handleClearAll}>
                  Clear
                </button>
                <button className="searchBtnPrimary" onClick={handleApplyAll}>
                  Apply
                </button>
              </div>
            </div>
          </section>

          <div className="searchPage__content">
            <div className="searchToolbar">
              <div className="searchToolbar__left">
                <h2 className="searchResultCount">{products?.length} Products</h2>
              </div>

              <div className="searchToolbar__right">
                <button
                  className="searchFilterButton"
                  onClick={toggleFilters}
                >
                  <IoFilter size={16} />
                  <span>Filters</span>
                </button>

                <div className="searchSortContainer">
                  <button
                    type="button"
                    className="searchSortButton"
                    onClick={() => setShowSortDropdown(!showSortDropdown)}
                  >
                    <span>
                      {selectedSortOption === "newest"
                        ? "Newest First"
                        : selectedSortOption === "price_low_to_high"
                          ? "Price Low to High"
                          : "Price High to Low"}
                    </span>

                    <IoIosArrowDown
                      size={18}
                      className={showSortDropdown ? "rotateArrow" : ""}
                    />
                  </button>

                  {showSortDropdown && (
                    <div className="searchSortDropdown">
                      <button
                        className="searchSortOption"
                        onClick={() => {
                          setSelectedSortOption("newest");
                          setShowSortDropdown(false);
                        }}
                      >
                        Newest First
                      </button>
                      <button
                        className="searchSortOption"
                        onClick={() => {
                          setSelectedSortOption("price_low_to_high");
                          setShowSortDropdown(false);
                        }}
                      >
                        Price Low to High
                      </button>
                      <button
                        className="searchSortOption"
                        onClick={() => {
                          setSelectedSortOption("price_high_to_low");
                          setShowSortDropdown(false);
                        }}
                      >
                        Price High to Low
                      </button>
                    </div>
                  )}
                </div>

                <div className="searchPage__form">
                  <div className="searchPage__inputWrap">
                    <FaSearch size={16} className="searchPage__inputIcon" />
                    <input
                      type="text"
                      placeholder="Search products..."
                      value={searchText}
                      onChange={(e) => setSearchText(e.target.value)}
                      className="searchPage__input with-icon"
                    />
                  </div>
                </div>
              </div>
            </div>

            <section className="searchPage__results">
              {products?.length > 0 ? (
                <div className="searchPage__list">
                  {products?.map((item) => (
                    <ProductCard key={item?._id} item={item} singleImage />
                  ))}
                </div>
              ) : (
                <EmptyState
                  icon={FaSearch}
                  title="No products found matching your search."
                  iconSize={40}
                />
              )}
            </section>
          </div>
        </main>
      </div>
      <CommonFooter />
      {loading && <FullScreenLoader text="Searching..." />}
    </div>
  );
};

export default SearchPage;
